/**
 * AdminCoinsConfig — Tenant Admin page for the loyalty rewards module.
 *
 * - Master enable/disable toggle
 * - Signup bonus (one-time on registration)
 * - Earn rate (coins per ₹1 spent — UI converts to "₹100 = N coins")
 * - Redeem rate (1 coin = ₹X)
 * - Min redemption + max % of order
 * - Manual grant (search a customer, add or revoke coins with a reason)
 * - Top customers by balance
 */
import { useEffect, useState } from "react";
import { Coins, Loader2, Save, Search, Plus, Minus } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";

const DEFAULT = {
    enabled: true, signup_bonus: 50, earn_per_rupee: 0.1, rupee_per_coin: 1.0,
    min_redeem_coins: 100, max_redeem_pct_of_order: 20, expiry_days: 0,
    customer_referral_coins: 0,
    customer_referral_wallet_bonus: 0,
};

export default function AdminCoinsConfig() {
    const [cfg, setCfg] = useState(DEFAULT);
    const [busy, setBusy] = useState(false);
    const [customers, setCustomers] = useState([]);
    const [query, setQuery] = useState("");
    const [grant, setGrant] = useState({ customer_id: "", delta: 100, note: "" });
    const [grantBusy, setGrantBusy] = useState(false);

    const load = async () => {
        const { data } = await api.get("/admin/coins-config");
        setCfg({ ...DEFAULT, ...data });
        const { data: list } = await api.get("/admin/coins/customers");
        setCustomers(list || []);
    };
    useEffect(() => { load(); }, []);

    const save = async () => {
        setBusy(true);
        try {
            await api.patch("/admin/coins-config", cfg);
            toast.success("Saved");
        } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); } finally { setBusy(false); }
    };

    const issueGrant = async () => {
        if (!grant.customer_id || !grant.delta) return;
        setGrantBusy(true);
        try {
            await api.post("/admin/coins/grant", grant);
            toast.success(`${grant.delta > 0 ? "Granted" : "Revoked"} ${Math.abs(grant.delta)} coins`);
            setGrant({ customer_id: "", delta: 100, note: "" });
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed");
        } finally { setGrantBusy(false); }
    };

    const filtered = customers.filter((c) =>
        !query || (c.mobile || "").includes(query) || (c.name || "").toLowerCase().includes(query.toLowerCase()),
    );

    const earnExample = Math.round((cfg.earn_per_rupee || 0) * 100);
    const redeemExample = (cfg.rupee_per_coin || 0) * 100;

    return (
        <div data-testid="admin-coins-config">
            <h1 className="text-2xl font-bold inline-flex items-center gap-2"><Coins size={20} className="text-amber-500" /> Coins / Rewards</h1>
            <p className="text-sm text-muted-foreground mt-1">Configure how customers earn and redeem loyalty coins. Use the manual adjustment block below to credit or revoke coins for any customer.</p>

            <div className="grid md:grid-cols-3 gap-6 mt-8">
                <div className="md:col-span-2 bg-card border border-border rounded-2xl p-5">
                    <div className="flex items-center justify-between mb-5">
                        <h2 className="text-base font-bold">Reward economy</h2>
                        <label className="inline-flex items-center gap-2 text-sm font-semibold">
                            <input data-testid="coins-enabled" type="checkbox" checked={!!cfg.enabled} onChange={(e) => setCfg({ ...cfg, enabled: e.target.checked })} className="accent-primary" />
                            Enabled
                        </label>
                    </div>

                    <fieldset disabled={!cfg.enabled} className="space-y-4 disabled:opacity-50">
                        <Field label="Signup bonus (coins)" hint="One-time grant when a new customer first logs in">
                            <input data-testid="coins-signup" type="number" value={cfg.signup_bonus} onChange={(e) => setCfg({ ...cfg, signup_bonus: e.target.value })} className="inp" />
                        </Field>

                        <Field
                            label="Customer referral bonus (coins)"
                            hint="When a new customer signs up using another customer's referral code AND their FIRST order is delivered, the referrer earns these coins. Set 0 to disable customer-to-customer rewards (shop referral commission is separate and unaffected)."
                        >
                            <input data-testid="coins-referral" type="number" value={cfg.customer_referral_coins} onChange={(e) => setCfg({ ...cfg, customer_referral_coins: e.target.value })} className="inp" />
                        </Field>

                        <Field
                            label="Customer referral bonus (₹ to wallet)"
                            hint="Same trigger as the coins bonus above (referred customer's first delivered order), but credited as real rupees to the referrer's Wallet instead of coins. Can be used together with or instead of the coins bonus — set 0 to disable."
                        >
                            <div className="flex items-center gap-2">
                                <span className="text-sm">₹</span>
                                <input data-testid="coins-referral-wallet" type="number" step="1" value={cfg.customer_referral_wallet_bonus} onChange={(e) => setCfg({ ...cfg, customer_referral_wallet_bonus: e.target.value })} className="inp w-32" />
                            </div>
                        </Field>

                        <Field label="Earn rate" hint={`Customer earns ${earnExample} coin${earnExample === 1 ? "" : "s"} for every ₹100 spent (${earnExample}% reward at 1:1 redemption).`}>
                            <div className="flex items-center gap-2">
                                <span className="text-sm">Coins per ₹1</span>
                                <input data-testid="coins-earn" type="number" step="0.01" value={cfg.earn_per_rupee} onChange={(e) => setCfg({ ...cfg, earn_per_rupee: e.target.value })} className="inp w-32" />
                            </div>
                        </Field>

                        <Field label="Redeem rate" hint={`100 coins = ₹${redeemExample} discount on next order.`}>
                            <div className="flex items-center gap-2">
                                <span className="text-sm">1 coin = ₹</span>
                                <input data-testid="coins-redeem" type="number" step="0.01" value={cfg.rupee_per_coin} onChange={(e) => setCfg({ ...cfg, rupee_per_coin: e.target.value })} className="inp w-32" />
                            </div>
                        </Field>

                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                            <Field label="Min redemption (coins)">
                                <input data-testid="coins-min" type="number" value={cfg.min_redeem_coins} onChange={(e) => setCfg({ ...cfg, min_redeem_coins: e.target.value })} className="inp" />
                            </Field>
                            <Field label="Max % of order">
                                <input data-testid="coins-max" type="number" value={cfg.max_redeem_pct_of_order} onChange={(e) => setCfg({ ...cfg, max_redeem_pct_of_order: e.target.value })} className="inp" />
                            </Field>
                            <Field label="Expiry (days, 0 = never)">
                                <input data-testid="coins-expiry" type="number" value={cfg.expiry_days} onChange={(e) => setCfg({ ...cfg, expiry_days: e.target.value })} className="inp" />
                            </Field>
                        </div>
                    </fieldset>

                    <button data-testid="coins-save" disabled={busy} onClick={save} className="mt-6 bg-primary text-primary-foreground rounded-xl px-5 py-2.5 font-bold text-sm inline-flex items-center gap-2 disabled:opacity-60">
                        {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save
                    </button>
                </div>

                {/* Best practice cheat-sheet — quick reference */}
                <aside className="bg-card border border-border rounded-2xl p-5 text-sm space-y-3 h-fit">
                    <h3 className="text-sm font-bold uppercase tracking-wider text-muted-foreground">Best-practice presets</h3>
                    <p className="text-xs text-muted-foreground">Tap to apply, then tweak.</p>
                    <PresetBtn cfg={cfg} setCfg={setCfg} name="Conservative" desc="5% earn · ₹0.50/coin"
                        v={{ earn_per_rupee: 0.05, rupee_per_coin: 0.5, max_redeem_pct_of_order: 10 }} />
                    <PresetBtn cfg={cfg} setCfg={setCfg} name="Standard" desc="10% earn · ₹1/coin"
                        v={{ earn_per_rupee: 0.1, rupee_per_coin: 1.0, max_redeem_pct_of_order: 20 }} />
                    <PresetBtn cfg={cfg} setCfg={setCfg} name="Aggressive" desc="15% earn · ₹1/coin"
                        v={{ earn_per_rupee: 0.15, rupee_per_coin: 1.0, max_redeem_pct_of_order: 25 }} />
                </aside>
            </div>

            {/* Manual grant */}
            <section className="mt-10 bg-card border border-border rounded-2xl p-5" data-testid="coins-grant-block">
                <h2 className="text-base font-bold">Grant or revoke coins</h2>
                <p className="text-xs text-muted-foreground mt-1">Use this for refunds, compensation, or VIP rewards.</p>

                <div className="mt-4 flex items-center gap-2 bg-muted rounded-xl px-3 py-2">
                    <Search size={16} className="text-muted-foreground" />
                    <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search customer by mobile or name" className="bg-transparent outline-none flex-1 text-sm" data-testid="coins-customer-search" />
                </div>

                <div className="mt-3 max-h-60 overflow-y-auto border border-border rounded-xl divide-y divide-border">
                    {filtered.length === 0 ? (
                        <div className="p-4 text-sm text-muted-foreground text-center">{query ? "No match" : "No customers yet"}</div>
                    ) : filtered.map((c) => (
                        <button
                            key={c.customer_id}
                            data-testid={`coins-customer-row-${c.customer_id}`}
                            onClick={() => setGrant({ ...grant, customer_id: c.customer_id })}
                            className={`w-full text-left flex items-center gap-3 p-3 hover:bg-muted/40 ${grant.customer_id === c.customer_id ? "bg-primary/10" : ""}`}
                        >
                            <div className="flex-1 min-w-0">
                                <p className="font-semibold text-sm">{c.name}</p>
                                <p className="text-xs text-muted-foreground">{c.mobile || "—"}</p>
                            </div>
                            <span className="text-sm font-bold text-amber-600 inline-flex items-center gap-1"><Coins size={12} /> {c.balance}</span>
                        </button>
                    ))}
                </div>

                <div className="mt-4 grid grid-cols-1 sm:grid-cols-[1fr_2fr_auto] gap-3 items-end">
                    <Field label="Coins (use negative to revoke)">
                        <input data-testid="coins-grant-delta" type="number" value={grant.delta} onChange={(e) => setGrant({ ...grant, delta: Number(e.target.value) })} className="inp" />
                    </Field>
                    <Field label="Reason (visible to customer)">
                        <input data-testid="coins-grant-note" value={grant.note} onChange={(e) => setGrant({ ...grant, note: e.target.value })} placeholder="Refund for order #1234" className="inp" />
                    </Field>
                    <button
                        data-testid="coins-grant-submit"
                        disabled={grantBusy || !grant.customer_id || !grant.delta}
                        onClick={issueGrant}
                        className={`rounded-xl px-5 py-2.5 font-bold text-sm inline-flex items-center gap-2 disabled:opacity-50 ${grant.delta >= 0 ? "bg-emerald-600 text-white" : "bg-red-600 text-white"}`}
                    >
                        {grantBusy ? <Loader2 size={14} className="animate-spin" /> : (grant.delta >= 0 ? <Plus size={14} /> : <Minus size={14} />)}
                        {grant.delta >= 0 ? "Grant" : "Revoke"}
                    </button>
                </div>
            </section>
        </div>
    );
}

function Field({ label, hint, children }) {
    return (
        <label className="block">
            <span className="text-xs font-semibold text-muted-foreground">{label}</span>
            <div className="mt-1">{children}</div>
            {hint && <p className="text-[11px] text-muted-foreground mt-1">{hint}</p>}
        </label>
    );
}

function PresetBtn({ cfg, setCfg, name, desc, v }) {
    const apply = () => setCfg({ ...cfg, ...v });
    return (
        <button onClick={apply} data-testid={`coins-preset-${name.toLowerCase()}`} className="w-full text-left border border-border rounded-xl p-3 hover:border-primary hover:bg-primary/5 transition">
            <p className="text-sm font-bold">{name}</p>
            <p className="text-xs text-muted-foreground">{desc}</p>
            <style>{`.inp{background:hsl(var(--muted));border-radius:0.75rem;padding:0.55rem 0.75rem;outline:none;font-size:0.875rem;width:100%}`}</style>
        </button>
    );
}
